import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTheme } from '@mui/material/styles';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, dataTableCardSx } from '../components/admin/adminListPageUi';
import { RoleAssignmentChip } from '../components/admin/iam/RoleAssignmentChip';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import {
  adminConsoleGroupsApi,
  adminConsoleUsersApi,
  adminIamApi,
  type AdminConsoleUser,
} from '../api/adminConsole';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../lib/adminSession';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ADMIN_IAM_ROLES_QUERY_KEY } from '../lib/adminRolesView';
import { buildUsersListParams, type UsersStatusFilter } from '../lib/adminIamQuery';
import { userAssignmentBadge } from '../lib/iamAssignmentStatus';
import {
  adminUserDeleteTooltip,
  canDeactivateAdminUser,
  canDeleteAdminUser,
  formatAdminUserLastLogin,
} from '../lib/adminUsersTable';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';

export const USERS_QUERY_KEY = ['adminConsoleUsers'] as const;

function StaffGroupMultiSelect({
  groups,
  value,
  onChange,
}: {
  groups: Array<{ id: string; name: string }>;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const toggle = (id: string, checked: boolean) => {
    onChange(checked ? [...new Set([...value, id])] : value.filter((item) => item !== id));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
      >
        Groups
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {groups.map((g) => {
          const selected = value.includes(g.id);
          return (
            <FormControlLabel
              key={g.id}
              sx={{
                m: 0,
                px: 1.25,
                py: 0.25,
                borderRadius: 1.5,
                border: 1,
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'action.selected' : 'transparent',
              }}
              control={
                <Checkbox size="small" checked={selected} onChange={(e) => toggle(g.id, e.target.checked)} />
              }
              label={g.name}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

const STATUS_FILTERS: Array<{ value: UsersStatusFilter; label: string }> = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'never_signed_in', label: 'Never signed in' },
];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const passwordSlotProps = (show: boolean, toggle: () => void) => ({
  htmlInput: {
    autoComplete: 'new-password',
    'data-1p-ignore': true,
    'data-lpignore': 'true',
    'data-form-type': 'other',
  },
  input: {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={toggle}
          onMouseDown={(event) => event.preventDefault()}
          edge="end"
          size="small"
        >
          {show ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  },
});

/**
 * Staff users (RBAC Phase 2): server-side paging, sort and filters on `GET /admin/users`. Rows open
 * the user detail page; create/edit/deactivate/delete need `iam.users.write`.
 */
export function AdminUsersPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const meQ = useAdminConsoleMe();
  const me = meQ.data;
  const isV2 = me?.authzModel === 'v2';
  const canWrite = hasAdminPermission(me, PermissionKey.IAM_USERS_WRITE);
  const canReadRoles = hasAdminPermission(me, PermissionKey.IAM_ROLES_READ);

  const [search, setSearch] = useState('');
  const q = useDebounced(search, 300);
  const [status, setStatus] = useState<UsersStatusFilter>('');
  const [roleId, setRoleId] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'displayName', sort: 'asc' }]);

  // A new filter starts from the first page. The page is stored with the filters it belongs to and
  // derived to 0 in the same render the filters change (no effect), so no request is sent with the
  // old offset.
  const filterKey = JSON.stringify([q, status, roleId]);
  const [pageState, setPageState] = useState<GridPaginationModel & { filterKey: string }>({
    page: 0,
    pageSize: 50,
    filterKey,
  });
  const pagination: GridPaginationModel = {
    page: pageState.filterKey === filterKey ? pageState.page : 0,
    pageSize: pageState.pageSize,
  };
  const setPagination = (m: GridPaginationModel) => setPageState({ page: m.page, pageSize: m.pageSize, filterKey });

  const params = useMemo(
    () =>
      buildUsersListParams({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sortModel,
        q,
        status,
        roleId,
      }),
    [pagination.page, pagination.pageSize, sortModel, q, status, roleId],
  );

  const usersQ = useQuery({
    queryKey: [...USERS_QUERY_KEY, params],
    queryFn: () => adminConsoleUsersApi.list(params),
    enabled: hasToken,
    placeholderData: keepPreviousData,
  });
  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: hasToken && canReadRoles,
    retry: false,
  });
  const groupsQ = useQuery({
    queryKey: ['adminConsoleGroups'],
    queryFn: () => adminConsoleGroupsApi.list(),
    enabled: hasToken && !isV2 && !!me,
  });

  const [actionError, setActionError] = useState<unknown>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminConsoleUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminConsoleUser | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const resetCreate = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setGroupIds([]);
    setShowCreatePassword(false);
  };

  const refreshUsers = () => qc.invalidateQueries({ queryKey: USERS_QUERY_KEY });

  const create = useMutation({
    mutationFn: () =>
      adminConsoleUsersApi.create({
        email: email.trim(),
        password,
        displayName: displayName.trim(),
        ...(isV2 ? {} : { groupIds }),
      }),
    onSuccess: async () => {
      setCreateOpen(false);
      resetCreate();
      await refreshUsers();
    },
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; body: Parameters<typeof adminConsoleUsersApi.patch>[1] }) =>
      adminConsoleUsersApi.patch(input.id, input.body),
    onSuccess: async () => {
      setEditUser(null);
      setPassword('');
      await refreshUsers();
    },
  });

  const quickPatch = useMutation({
    mutationFn: (input: { id: string; body: { isActive?: boolean; isMonitored?: boolean } }) =>
      adminConsoleUsersApi.patch(input.id, input.body),
    onMutate: () => setActionError(null),
    onError: (err) => setActionError(err),
    onSettled: async (_d, _e, input) => {
      await refreshUsers();
      if (me?.id === input.id) await qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
    },
  });

  const removeUser = useMutation({
    mutationFn: (id: string) => adminConsoleUsersApi.remove(id),
    onSuccess: async () => {
      setDeleteUser(null);
      await refreshUsers();
      await qc.invalidateQueries({ queryKey: ['adminConsoleGroups'] });
    },
  });

  const users = usersQ.data?.items ?? [];
  const groups = groupsQ.data?.items ?? [];

  const columns = useMemo<GridColDef<AdminConsoleUser>[]>(() => {
    const cols: GridColDef<AdminConsoleUser>[] = [
      {
        field: 'displayName',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.displayName}
          </Typography>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 200,
        // A real link so keyboard users can open the user page (row click is mouse-only).
        renderCell: ({ row }) => (
          <Typography
            component={RouterLink}
            to={`/admin/users/${encodeURIComponent(row.id)}`}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            variant="body2"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.8125rem',
              color: 'inherit',
              textDecoration: 'none',
              '&:hover, &:focus-visible': { textDecoration: 'underline' },
            }}
          >
            {row.email}
          </Typography>
        ),
      },
      {
        field: 'isActive',
        headerName: 'Status',
        sortable: false,
        width: 130,
        renderCell: ({ row }) => {
          const lastSuperAdmin = !canDeactivateAdminUser(row, users);
          if (!canWrite) {
            return (
              <Chip size="small" variant="outlined" color={row.isActive ? 'success' : 'default'} label={row.isActive ? 'Active' : 'Inactive'} />
            );
          }
          const busy = quickPatch.isPending && quickPatch.variables?.id === row.id;
          return (
            <Tooltip
              title={
                lastSuperAdmin
                  ? 'Cannot deactivate the last active super administrator'
                  : row.isActive
                    ? 'Deactivate this user'
                    : 'Activate this user'
              }
            >
              <span onClick={(e) => e.stopPropagation()}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={row.isActive}
                      disabled={busy || lastSuperAdmin}
                      onChange={(_, checked) => quickPatch.mutate({ id: row.id, body: { isActive: checked } })}
                      inputProps={{ 'aria-label': `${row.email} ${row.isActive ? 'active' : 'inactive'}` }}
                    />
                  }
                  label={
                    <Typography variant="caption" color={row.isActive ? 'success.main' : 'text.secondary'} sx={{ fontWeight: 600 }}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last login',
        width: 180,
        renderCell: ({ row }) => (
          <Typography variant="body2" color={row.lastLoginAt ? 'text.primary' : 'text.secondary'}>
            {formatAdminUserLastLogin(row)}
          </Typography>
        ),
      },
    ];
    if (isV2 || users.some((u) => (u.roles ?? []).length > 0)) {
      cols.push(
        {
          field: 'roles',
          headerName: 'Roles',
          sortable: false,
          flex: 1.4,
          minWidth: 220,
          renderCell: ({ row }) =>
            (row.roles ?? []).length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            ) : (
              <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.75 }}>
                {(row.roles ?? []).map((r) => (
                  <RoleAssignmentChip key={r.assignmentId ?? r.id} name={r.name} status={r.status} expiresAt={r.expiresAt} />
                ))}
              </Stack>
            ),
        },
        {
          field: 'assignmentStatus',
          headerName: 'Access',
          sortable: false,
          width: 110,
          renderCell: ({ row }) => {
            const badge = userAssignmentBadge(row.assignmentStatus);
            return badge ? (
              <Chip size="small" variant="outlined" color={badge.color} label={badge.label} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            );
          },
        },
      );
    } else {
      cols.push({
        field: 'groups',
        headerName: 'Groups',
        sortable: false,
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.75 }}>
            {(row.groups ?? []).length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {row.role === 'SUPER_ADMIN' ? 'All access' : 'None'}
              </Typography>
            ) : (
              (row.groups ?? []).map((g) => <Chip key={g.id} label={g.name} size="small" />)
            )}
          </Stack>
        ),
      });
    }
    if (canWrite) {
      cols.push(
        {
          field: 'isMonitored',
          headerName: 'Monitored',
          sortable: false,
          width: 110,
          renderCell: ({ row }) => {
            const on = row.isMonitored ?? true;
            const busy = quickPatch.isPending && quickPatch.variables?.id === row.id;
            return (
              <span onClick={(e) => e.stopPropagation()}>
                <Switch
                  size="small"
                  checked={on}
                  disabled={busy}
                  onChange={(_, checked) => quickPatch.mutate({ id: row.id, body: { isMonitored: checked } })}
                  inputProps={{ 'aria-label': `${row.email} ${on ? 'monitored' : 'not monitored'}` }}
                />
              </span>
            );
          },
        },
        {
          field: 'actions',
          headerName: '',
          sortable: false,
          width: 150,
          align: 'right',
          renderCell: ({ row }) => {
            const canDelete = canDeleteAdminUser(row, users, me?.id);
            return (
              <Stack direction="row" gap={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  onClick={() => {
                    setEditUser(row);
                    setDisplayName(row.displayName);
                    setPassword('');
                    setShowEditPassword(false);
                    setGroupIds((row.groups ?? []).map((g) => g.id));
                  }}
                >
                  Edit
                </Button>
                <Tooltip title={adminUserDeleteTooltip(row, users, me?.id)}>
                  <span>
                    <Button size="small" color="error" disabled={!canDelete} onClick={() => setDeleteUser(row)}>
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            );
          },
        },
      );
    }
    return cols;
  }, [isV2, users, canWrite, me?.id, quickPatch]);

  const headerActions = useMemo(
    () => (
      <>
        <Chip label={`${usersQ.data?.total ?? 0} users`} size="small" />
        {canWrite && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              resetCreate();
              setCreateOpen(true);
            }}
          >
            Onboard employee
          </Button>
        )}
      </>
    ),
    [usersQ.data?.total, canWrite],
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to manage users.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Users"
        subtitle={
          isV2
            ? 'Staff accounts. Open a user to manage roles, see effective permissions and audit history.'
            : 'Employees onboarded by a super administrator. Assign groups to control tabs and actions.'
        }
        endAdornment={headerActions}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search email or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: { sm: '0 1 340px' } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as UsersStatusFilter)}
          sx={{ minWidth: 170 }}
        >
          {STATUS_FILTERS.map((s) => (
            <MenuItem key={s.value || 'any'} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        {canReadRoles && (
          <TextField
            select
            size="small"
            label="Role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">Any role</MenuItem>
            {(rolesQ.data?.items ?? []).map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {isV2 && hasAdminPermission(me, PermissionKey.IAM_USERS_READ) && (
          <Button component={RouterLink} to="/admin/access/expiring" size="small" sx={{ ml: { sm: 'auto' } }}>
            Expiring access
          </Button>
        )}
      </Stack>

      <IamErrorAlert error={usersQ.error ?? actionError ?? removeUser.error} sx={{ mb: 2 }} />

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        <DataGrid
          rows={users}
          columns={columns}
          getRowId={(r) => r.id}
          rowCount={usersQ.data?.total ?? 0}
          loading={usersQ.isFetching}
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={sortModel}
          onSortModelChange={(m) => {
            setSortModel(m);
            setPagination({ ...pagination, page: 0 });
          }}
          pageSizeOptions={[25, 50, 100]}
          disableColumnFilter
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          getRowClassName={({ row }) => (row.isActive ? '' : 'row-inactive')}
          onRowClick={({ row }) => void navigate(`/admin/users/${encodeURIComponent(row.id)}`)}
          localeText={{ noRowsLabel: q || status || roleId ? 'No users match these filters.' : 'No users yet.' }}
          sx={{
            border: 0,
            minHeight: 360,
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 0.5 },
            '& .row-inactive': { opacity: 0.7 },
          }}
        />
      </Card>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetCreate();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
            Onboard employee
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isV2
              ? 'Creates the account in auth-server. Grant roles afterwards from the user page.'
              : 'Creates the account in auth-server and assigns optional groups.'}
          </Typography>
        </DialogTitle>
        <DialogContent
          key={createOpen ? 'onboard-open' : 'onboard-closed'}
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5, position: 'relative', overflow: 'hidden' }}
        >
          <Box
            aria-hidden
            component="div"
            sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}
          >
            <input type="text" name="username" autoComplete="username" tabIndex={-1} />
            <input type="password" name="password" autoComplete="current-password" tabIndex={-1} />
          </Box>
          <IamErrorAlert error={create.error} />
          <TextField
            label="Email"
            type="email"
            name="employee-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            fullWidth
            autoComplete="off"
            slotProps={{ htmlInput: { autoComplete: 'off', 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-form-type': 'other' } }}
          />
          <TextField
            label="Display name"
            name="employee-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            size="small"
            fullWidth
            autoComplete="off"
            slotProps={{ htmlInput: { autoComplete: 'off', 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-form-type': 'other' } }}
          />
          <TextField
            label="Temporary password"
            type={showCreatePassword ? 'text' : 'password'}
            name="employee-temporary-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            fullWidth
            helperText="Min 12 characters with upper, lower, digit, and special character."
            slotProps={passwordSlotProps(showCreatePassword, () => setShowCreatePassword((v) => !v))}
          />
          {!isV2 && <StaffGroupMultiSelect groups={groups} value={groupIds} onChange={setGroupIds} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setCreateOpen(false);
              resetCreate();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editUser)}
        onClose={() => {
          setEditUser(null);
          setShowEditPassword(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
            Edit user
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {editUser?.email}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5, overflow: 'hidden' }}>
          <IamErrorAlert error={patch.error} />
          {editUser && (
            <>
              <TextField
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                size="small"
                fullWidth
              />
              {isV2 ? (
                <Alert severity="info">
                  Roles are managed on the{' '}
                  <RouterLink to={`/admin/users/${encodeURIComponent(editUser.id)}`}>user page</RouterLink>.
                </Alert>
              ) : (
                editUser.role !== 'SUPER_ADMIN' && (
                  <StaffGroupMultiSelect groups={groups} value={groupIds} onChange={setGroupIds} />
                )
              )}
              <TextField
                label="Reset password"
                type={showEditPassword ? 'text' : 'password'}
                name="employee-reset-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="small"
                fullWidth
                helperText="Leave blank to keep the current password."
                slotProps={passwordSlotProps(showEditPassword, () => setShowEditPassword((v) => !v))}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setEditUser(null);
              setShowEditPassword(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!editUser || patch.isPending}
            onClick={() => {
              if (!editUser) return;
              patch.mutate({
                id: editUser.id,
                body: {
                  displayName: displayName.trim(),
                  ...(password ? { password } : {}),
                  ...(isV2 || editUser.role === 'SUPER_ADMIN' ? {} : { groupIds }),
                },
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteUser)} onClose={() => setDeleteUser(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
            Delete user
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <IamErrorAlert error={removeUser.error} sx={{ mb: 2 }} />
          <Typography variant="body2">
            Mark <strong>{deleteUser?.displayName}</strong> ({deleteUser?.email}) as deleted? They will
            disappear from this list and will not be able to sign in. The row stays in the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleteUser || removeUser.isPending}
            onClick={() => deleteUser && removeUser.mutate(deleteUser.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
